import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const languages = [
  { code: "en", label: "EN" },
  { code: "fr", label: "FR" },
  { code: "es", label: "ES" },
  { code: "de", label: "DE" },
];

// URL mapping for pages that have separate language-specific URLs
const languagePageMap: Record<string, Record<string, string>> = {
  // Home pages
  "/de": { en: "/en", fr: "/fr", es: "/es" },
  "/en": { de: "/de", fr: "/fr", es: "/es" },
  "/fr": { de: "/de", en: "/en", es: "/es" },
  "/es": { de: "/de", en: "/en", fr: "/fr" },
  "/de/datenschutz": { en: "/en/privacy", fr: "/fr/politique-de-confidentialite", es: "/es/politica-de-privacidad" },
  "/en/privacy": { de: "/de/datenschutz", fr: "/fr/politique-de-confidentialite", es: "/es/politica-de-privacidad" },
  "/fr/politique-de-confidentialite": { de: "/de/datenschutz", en: "/en/privacy", es: "/es/politica-de-privacidad" },
  "/es/politica-de-privacidad": { de: "/de/datenschutz", en: "/en/privacy", fr: "/fr/politique-de-confidentialite" },
  "/de/impressum": { en: "/en/legal-notice", fr: "/fr/mentions-legales", es: "/es/aviso-legal" },
  "/en/legal-notice": { de: "/de/impressum", fr: "/fr/mentions-legales", es: "/es/aviso-legal" },
  "/fr/mentions-legales": { de: "/de/impressum", en: "/en/legal-notice", es: "/es/aviso-legal" },
  "/es/aviso-legal": { de: "/de/impressum", en: "/en/legal-notice", fr: "/fr/mentions-legales" },
  // About pages
  "/de/ueber-uns": { en: "/en/about-us", fr: "/fr/a-propos", es: "/es/sobre-nosotros" },
  "/en/about-us": { de: "/de/ueber-uns", fr: "/fr/a-propos", es: "/es/sobre-nosotros" },
  "/fr/a-propos": { de: "/de/ueber-uns", en: "/en/about-us", es: "/es/sobre-nosotros" },
  "/es/sobre-nosotros": { de: "/de/ueber-uns", en: "/en/about-us", fr: "/fr/a-propos" },
  // About sub-pages — Management
  "/de/ueber-uns/management": { en: "/en/about-us/management", fr: "/fr/a-propos/direction", es: "/es/sobre-nosotros/direccion" },
  "/en/about-us/management": { de: "/de/ueber-uns/management", fr: "/fr/a-propos/direction", es: "/es/sobre-nosotros/direccion" },
  "/fr/a-propos/direction": { de: "/de/ueber-uns/management", en: "/en/about-us/management", es: "/es/sobre-nosotros/direccion" },
  "/es/sobre-nosotros/direccion": { de: "/de/ueber-uns/management", en: "/en/about-us/management", fr: "/fr/a-propos/direction" },
  // About sub-pages — Core Values
  "/de/ueber-uns/grundwerte": { en: "/en/about-us/core-values", fr: "/fr/a-propos/valeurs-fondamentales", es: "/es/sobre-nosotros/valores-fundamentales" },
  "/en/about-us/core-values": { de: "/de/ueber-uns/grundwerte", fr: "/fr/a-propos/valeurs-fondamentales", es: "/es/sobre-nosotros/valores-fundamentales" },
  "/fr/a-propos/valeurs-fondamentales": { de: "/de/ueber-uns/grundwerte", en: "/en/about-us/core-values", es: "/es/sobre-nosotros/valores-fundamentales" },
  "/es/sobre-nosotros/valores-fundamentales": { de: "/de/ueber-uns/grundwerte", en: "/en/about-us/core-values", fr: "/fr/a-propos/valeurs-fondamentales" },
  // About sub-pages — Mission/Vision
  "/de/ueber-uns/mission-vision": { en: "/en/about-us/mission-vision", fr: "/fr/a-propos/mission-vision", es: "/es/sobre-nosotros/mision-vision" },
  "/en/about-us/mission-vision": { de: "/de/ueber-uns/mission-vision", fr: "/fr/a-propos/mission-vision", es: "/es/sobre-nosotros/mision-vision" },
  "/fr/a-propos/mission-vision": { de: "/de/ueber-uns/mission-vision", en: "/en/about-us/mission-vision", es: "/es/sobre-nosotros/mision-vision" },
  "/es/sobre-nosotros/mision-vision": { de: "/de/ueber-uns/mission-vision", en: "/en/about-us/mission-vision", fr: "/fr/a-propos/mission-vision" },
  // Partnership pages
  "/de/partnerschaft": { en: "/en/partnership", fr: "/fr/partenariat", es: "/es/colaboracion" },
  "/en/partnership": { de: "/de/partnerschaft", fr: "/fr/partenariat", es: "/es/colaboracion" },
  "/fr/partenariat": { de: "/de/partnerschaft", en: "/en/partnership", es: "/es/colaboracion" },
  "/es/colaboracion": { de: "/de/partnerschaft", en: "/en/partnership", fr: "/fr/partenariat" },
  // Contact pages (language-prefixed)
  "/de/kontakt": { en: "/en/contact", fr: "/fr/contact", es: "/es/contacto" },
  "/en/contact": { de: "/de/kontakt", fr: "/fr/contact", es: "/es/contacto" },
  "/fr/contact": { de: "/de/kontakt", en: "/en/contact", es: "/es/contacto" },
  "/es/contacto": { de: "/de/kontakt", en: "/en/contact", fr: "/fr/contact" },
  // Services pages (language-prefixed)
  "/de/services": { en: "/en/services", fr: "/fr/services", es: "/es/servicios" },
  "/en/services": { de: "/de/services", fr: "/fr/services", es: "/es/servicios" },
  "/fr/services": { de: "/de/services", en: "/en/services", es: "/es/servicios" },
  "/es/servicios": { de: "/de/services", en: "/en/services", fr: "/fr/services" },
  // Business Development overview pages
  "/de/services/business-development": { en: "/en/services/business-development", fr: "/fr/services/developpement-commercial", es: "/es/servicios/desarrollo-de-negocio" },
  "/en/services/business-development": { de: "/de/services/business-development", fr: "/fr/services/developpement-commercial", es: "/es/servicios/desarrollo-de-negocio" },
  "/fr/services/developpement-commercial": { de: "/de/services/business-development", en: "/en/services/business-development", es: "/es/servicios/desarrollo-de-negocio" },
  "/es/servicios/desarrollo-de-negocio": { de: "/de/services/business-development", en: "/en/services/business-development", fr: "/fr/services/developpement-commercial" },
  // Business Development subpages — Geschaeftspraesenz / Business Presence / Présence commerciale / Presencia empresarial
  "/de/services/business-development/geschaeftspraesenz": { en: "/en/services/business-development/business-presence", fr: "/fr/services/developpement-commercial/presence-commerciale", es: "/es/servicios/desarrollo-de-negocio/presencia-empresarial" },
  "/en/services/business-development/business-presence": { de: "/de/services/business-development/geschaeftspraesenz", fr: "/fr/services/developpement-commercial/presence-commerciale", es: "/es/servicios/desarrollo-de-negocio/presencia-empresarial" },
  "/fr/services/developpement-commercial/presence-commerciale": { de: "/de/services/business-development/geschaeftspraesenz", en: "/en/services/business-development/business-presence", es: "/es/servicios/desarrollo-de-negocio/presencia-empresarial" },
  "/es/servicios/desarrollo-de-negocio/presencia-empresarial": { de: "/de/services/business-development/geschaeftspraesenz", en: "/en/services/business-development/business-presence", fr: "/fr/services/developpement-commercial/presence-commerciale" },
  // Business Development subpages — Full-Service-Angebot / Full-Service Package / Offre complète / Servicio completo
  "/de/services/business-development/full-service-angebot": { en: "/en/services/business-development/full-service-offer", fr: "/fr/services/developpement-commercial/offre-complete", es: "/es/servicios/desarrollo-de-negocio/paquete-de-servicio-completo" },
  "/en/services/business-development/full-service-offer": { de: "/de/services/business-development/full-service-angebot", fr: "/fr/services/developpement-commercial/offre-complete", es: "/es/servicios/desarrollo-de-negocio/paquete-de-servicio-completo" },
  "/fr/services/developpement-commercial/offre-complete": { de: "/de/services/business-development/full-service-angebot", en: "/en/services/business-development/full-service-offer", es: "/es/servicios/desarrollo-de-negocio/paquete-de-servicio-completo" },
  "/es/servicios/desarrollo-de-negocio/paquete-de-servicio-completo": { de: "/de/services/business-development/full-service-angebot", en: "/en/services/business-development/full-service-offer", fr: "/fr/services/developpement-commercial/offre-complete" },
  // Business Development subpages — Consulting / Conseil / Consultoría
  "/de/services/business-development/consulting": { en: "/en/services/business-development/consulting-services", fr: "/fr/services/developpement-commercial/conseil", es: "/es/servicios/desarrollo-de-negocio/consultoria" },
  "/en/services/business-development/consulting-services": { de: "/de/services/business-development/consulting", fr: "/fr/services/developpement-commercial/conseil", es: "/es/servicios/desarrollo-de-negocio/consultoria" },
  "/fr/services/developpement-commercial/conseil": { de: "/de/services/business-development/consulting", en: "/en/services/business-development/consulting-services", es: "/es/servicios/desarrollo-de-negocio/consultoria" },
  "/es/servicios/desarrollo-de-negocio/consultoria": { de: "/de/services/business-development/consulting", en: "/en/services/business-development/consulting-services", fr: "/fr/services/developpement-commercial/conseil" },
  // Standalone Consulting pages
  "/de/services/consulting": { en: "/en/services/consulting-services", fr: "/fr/services/conseil", es: "/es/servicios/consultoria" },
  "/en/services/consulting-services": { de: "/de/services/consulting", fr: "/fr/services/conseil", es: "/es/servicios/consultoria" },
  "/fr/services/conseil": { de: "/de/services/consulting", en: "/en/services/consulting-services", es: "/es/servicios/consultoria" },
  "/es/servicios/consultoria": { de: "/de/services/consulting", en: "/en/services/consulting-services", fr: "/fr/services/conseil" },
  // Business Development subpages — Produktmanagement / Product Management / Gestion de produits / Gestión de productos
  "/de/services/business-development/produktmanagement": { en: "/en/services/business-development/product-management", fr: "/fr/services/developpement-commercial/gestion-de-produits", es: "/es/servicios/desarrollo-de-negocio/gestion-de-productos" },
  "/en/services/business-development/product-management": { de: "/de/services/business-development/produktmanagement", fr: "/fr/services/developpement-commercial/gestion-de-produits", es: "/es/servicios/desarrollo-de-negocio/gestion-de-productos" },
  "/fr/services/developpement-commercial/gestion-de-produits": { de: "/de/services/business-development/produktmanagement", en: "/en/services/business-development/product-management", es: "/es/servicios/desarrollo-de-negocio/gestion-de-productos" },
  "/es/servicios/desarrollo-de-negocio/gestion-de-productos": { de: "/de/services/business-development/produktmanagement", en: "/en/services/business-development/product-management", fr: "/fr/services/developpement-commercial/gestion-de-produits" },
  // Business Development subpages — Projektmanagement / Project Management / Gestion de projets / Gestión de proyectos
  "/de/services/business-development/projektmanagement": { en: "/en/services/business-development/project-management", fr: "/fr/services/developpement-commercial/gestion-de-projets", es: "/es/servicios/desarrollo-de-negocio/gestion-de-proyectos" },
  "/en/services/business-development/project-management": { de: "/de/services/business-development/projektmanagement", fr: "/fr/services/developpement-commercial/gestion-de-projets", es: "/es/servicios/desarrollo-de-negocio/gestion-de-proyectos" },
  "/fr/services/developpement-commercial/gestion-de-projets": { de: "/de/services/business-development/projektmanagement", en: "/en/services/business-development/project-management", es: "/es/servicios/desarrollo-de-negocio/gestion-de-proyectos" },
  "/es/servicios/desarrollo-de-negocio/gestion-de-proyectos": { de: "/de/services/business-development/projektmanagement", en: "/en/services/business-development/project-management", fr: "/fr/services/developpement-commercial/gestion-de-projets" },
  // Business Development subpages — Marktanalysen / Market Analyses / Analyses de marché / Análisis de mercado
  "/de/services/business-development/marktanalysen": { en: "/en/services/business-development/market-research", fr: "/fr/services/developpement-commercial/analyses-de-marche", es: "/es/servicios/desarrollo-de-negocio/analisis-de-mercado" },
  "/en/services/business-development/market-research": { de: "/de/services/business-development/marktanalysen", fr: "/fr/services/developpement-commercial/analyses-de-marche", es: "/es/servicios/desarrollo-de-negocio/analisis-de-mercado" },
  "/fr/services/developpement-commercial/analyses-de-marche": { de: "/de/services/business-development/marktanalysen", en: "/en/services/business-development/market-research", es: "/es/servicios/desarrollo-de-negocio/analisis-de-mercado" },
  "/es/servicios/desarrollo-de-negocio/analisis-de-mercado": { de: "/de/services/business-development/marktanalysen", en: "/en/services/business-development/market-research", fr: "/fr/services/developpement-commercial/analyses-de-marche" },
  // Business Development subpages — Markteintritt / Market Entry Strategies / Stratégies d'entrée / Estrategias de entrada
  "/de/services/business-development/markteintritt": { en: "/en/services/business-development/market-entry-strategies", fr: "/fr/services/developpement-commercial/strategies-entree-marche", es: "/es/servicios/desarrollo-de-negocio/estrategias-entrada-mercado" },
  "/en/services/business-development/market-entry-strategies": { de: "/de/services/business-development/markteintritt", fr: "/fr/services/developpement-commercial/strategies-entree-marche", es: "/es/servicios/desarrollo-de-negocio/estrategias-entrada-mercado" },
  "/fr/services/developpement-commercial/strategies-entree-marche": { de: "/de/services/business-development/markteintritt", en: "/en/services/business-development/market-entry-strategies", es: "/es/servicios/desarrollo-de-negocio/estrategias-entrada-mercado" },
  "/es/servicios/desarrollo-de-negocio/estrategias-entrada-mercado": { de: "/de/services/business-development/markteintritt", en: "/en/services/business-development/market-entry-strategies", fr: "/fr/services/developpement-commercial/strategies-entree-marche" },
  // Business Development subpages — Import-Management / Import Management / Gestion des importations / Gestión de importaciones
  "/de/services/business-development/importmanagement": { en: "/en/services/business-development/import-management", fr: "/fr/services/developpement-commercial/gestion-des-importations", es: "/es/servicios/desarrollo-de-negocio/gestion-de-importaciones" },
  "/en/services/business-development/import-management": { de: "/de/services/business-development/importmanagement", fr: "/fr/services/developpement-commercial/gestion-des-importations", es: "/es/servicios/desarrollo-de-negocio/gestion-de-importaciones" },
  "/fr/services/developpement-commercial/gestion-des-importations": { de: "/de/services/business-development/importmanagement", en: "/en/services/business-development/import-management", es: "/es/servicios/desarrollo-de-negocio/gestion-de-importaciones" },
  "/es/servicios/desarrollo-de-negocio/gestion-de-importaciones": { de: "/de/services/business-development/importmanagement", en: "/en/services/business-development/import-management", fr: "/fr/services/developpement-commercial/gestion-des-importations" },
  // Business Development subpages — Internationaler Handel / International Trade / Commerce international / Comercio internacional
  "/de/services/business-development/internationaler-handel": { en: "/en/services/business-development/international-trade", fr: "/fr/services/developpement-commercial/commerce-international", es: "/es/servicios/desarrollo-de-negocio/comercio-internacional" },
  "/en/services/business-development/international-trade": { de: "/de/services/business-development/internationaler-handel", fr: "/fr/services/developpement-commercial/commerce-international", es: "/es/servicios/desarrollo-de-negocio/comercio-internacional" },
  "/fr/services/developpement-commercial/commerce-international": { de: "/de/services/business-development/internationaler-handel", en: "/en/services/business-development/international-trade", es: "/es/servicios/desarrollo-de-negocio/comercio-internacional" },
  "/es/servicios/desarrollo-de-negocio/comercio-internacional": { de: "/de/services/business-development/internationaler-handel", en: "/en/services/business-development/international-trade", fr: "/fr/services/developpement-commercial/commerce-international" },
  // Business Development subpages — Technischer Support / Technical Support / Support technique / Soporte técnico
  "/de/services/business-development/technischer-support": { en: "/en/services/business-development/technical-support", fr: "/fr/services/developpement-commercial/support-technique", es: "/es/servicios/desarrollo-de-negocio/soporte-tecnico" },
  "/en/services/business-development/technical-support": { de: "/de/services/business-development/technischer-support", fr: "/fr/services/developpement-commercial/support-technique", es: "/es/servicios/desarrollo-de-negocio/soporte-tecnico" },
  "/fr/services/developpement-commercial/support-technique": { de: "/de/services/business-development/technischer-support", en: "/en/services/business-development/technical-support", es: "/es/servicios/desarrollo-de-negocio/soporte-tecnico" },
  "/es/servicios/desarrollo-de-negocio/soporte-tecnico": { de: "/de/services/business-development/technischer-support", en: "/en/services/business-development/technical-support", fr: "/fr/services/developpement-commercial/support-technique" },
  // Interim Management pages
  "/de/services/interim-management": { en: "/en/services/interim-management", fr: "/fr/services/management-de-transition", es: "/es/servicios/interim-management" },
  "/en/services/interim-management": { de: "/de/services/interim-management", fr: "/fr/services/management-de-transition", es: "/es/servicios/interim-management" },
  "/fr/services/management-de-transition": { de: "/de/services/interim-management", en: "/en/services/interim-management", es: "/es/servicios/interim-management" },
  "/es/servicios/interim-management": { de: "/de/services/interim-management", en: "/en/services/interim-management", fr: "/fr/services/management-de-transition" },
  // Training & Coaching pages
  "/de/services/training-coaching": { en: "/en/services/training-coaching", fr: "/fr/services/formation-et-coaching", es: "/es/servicios/formacion-y-coaching" },
  "/en/services/training-coaching": { de: "/de/services/training-coaching", fr: "/fr/services/formation-et-coaching", es: "/es/servicios/formacion-y-coaching" },
  "/fr/services/formation-et-coaching": { de: "/de/services/training-coaching", en: "/en/services/training-coaching", es: "/es/servicios/formacion-y-coaching" },
  "/es/servicios/formacion-y-coaching": { de: "/de/services/training-coaching", en: "/en/services/training-coaching", fr: "/fr/services/formation-et-coaching" },
};

// Map page URLs to their corresponding language
const urlToLanguage: Record<string, string> = {
  // Home pages
  "/de": "de",
  "/en": "en",
  "/fr": "fr",
  "/es": "es",
  "/de/datenschutz": "de",
  "/en/privacy": "en",
  "/fr/politique-de-confidentialite": "fr",
  "/es/politica-de-privacidad": "es",
  "/de/impressum": "de",
  "/en/legal-notice": "en",
  "/fr/mentions-legales": "fr",
  "/es/aviso-legal": "es",
  // About pages
  "/de/ueber-uns": "de",
  "/en/about-us": "en",
  "/fr/a-propos": "fr",
  "/es/sobre-nosotros": "es",
  // About sub-pages
  "/de/ueber-uns/management": "de",
  "/en/about-us/management": "en",
  "/fr/a-propos/direction": "fr",
  "/es/sobre-nosotros/direccion": "es",
  "/de/ueber-uns/grundwerte": "de",
  "/en/about-us/core-values": "en",
  "/fr/a-propos/valeurs-fondamentales": "fr",
  "/es/sobre-nosotros/valores-fundamentales": "es",
  "/de/ueber-uns/mission-vision": "de",
  "/en/about-us/mission-vision": "en",
  "/fr/a-propos/mission-vision": "fr",
  "/es/sobre-nosotros/mision-vision": "es",
  // Partnership pages
  "/de/partnerschaft": "de",
  "/en/partnership": "en",
  "/fr/partenariat": "fr",
  "/es/colaboracion": "es",
  // Contact pages (language-prefixed)
  "/de/kontakt": "de",
  "/en/contact": "en",
  "/fr/contact": "fr",
  "/es/contacto": "es",
  // Services pages (language-prefixed)
  "/de/services": "de",
  "/en/services": "en",
  "/fr/services": "fr",
  "/es/servicios": "es",
  // Standalone Consulting pages
  "/de/services/consulting": "de",
  "/en/services/consulting-services": "en",
  "/fr/services/conseil": "fr",
  "/es/servicios/consultoria": "es",
  // Business Development pages (DE)
  "/de/services/business-development": "de",
  "/de/services/business-development/geschaeftspraesenz": "de",
  "/de/services/business-development/full-service-angebot": "de",
  "/de/services/business-development/consulting": "de",
  "/de/services/business-development/produktmanagement": "de",
  "/de/services/business-development/projektmanagement": "de",
  "/de/services/business-development/marktanalysen": "de",
  "/de/services/business-development/markteintritt": "de",
  "/de/services/business-development/importmanagement": "de",
  "/de/services/business-development/internationaler-handel": "de",
  "/de/services/business-development/technischer-support": "de",
  // Business Development pages (EN)
  "/en/services/business-development": "en",
  "/en/services/business-development/business-presence": "en",
  "/en/services/business-development/full-service-offer": "en",
  "/en/services/business-development/consulting-services": "en",
  "/en/services/business-development/product-management": "en",
  "/en/services/business-development/project-management": "en",
  "/en/services/business-development/market-research": "en",
  "/en/services/business-development/market-entry-strategies": "en",
  "/en/services/business-development/import-management": "en",
  "/en/services/business-development/international-trade": "en",
  "/en/services/business-development/technical-support": "en",
  // Business Development pages (FR)
  "/fr/services/developpement-commercial": "fr",
  "/fr/services/developpement-commercial/presence-commerciale": "fr",
  "/fr/services/developpement-commercial/offre-complete": "fr",
  "/fr/services/developpement-commercial/conseil": "fr",
  "/fr/services/developpement-commercial/gestion-de-produits": "fr",
  "/fr/services/developpement-commercial/gestion-de-projets": "fr",
  "/fr/services/developpement-commercial/analyses-de-marche": "fr",
  "/fr/services/developpement-commercial/strategies-entree-marche": "fr",
  "/fr/services/developpement-commercial/gestion-des-importations": "fr",
  "/fr/services/developpement-commercial/commerce-international": "fr",
  "/fr/services/developpement-commercial/support-technique": "fr",
  // Business Development pages (ES)
  "/es/servicios/desarrollo-de-negocio": "es",
  "/es/servicios/desarrollo-de-negocio/presencia-empresarial": "es",
  "/es/servicios/desarrollo-de-negocio/paquete-de-servicio-completo": "es",
  "/es/servicios/desarrollo-de-negocio/consultoria": "es",
  "/es/servicios/desarrollo-de-negocio/gestion-de-productos": "es",
  "/es/servicios/desarrollo-de-negocio/gestion-de-proyectos": "es",
  "/es/servicios/desarrollo-de-negocio/analisis-de-mercado": "es",
  "/es/servicios/desarrollo-de-negocio/estrategias-entrada-mercado": "es",
  "/es/servicios/desarrollo-de-negocio/gestion-de-importaciones": "es",
  "/es/servicios/desarrollo-de-negocio/comercio-internacional": "es",
  "/es/servicios/desarrollo-de-negocio/soporte-tecnico": "es",
  // Interim Management pages
  "/de/services/interim-management": "de",
  "/en/services/interim-management": "en",
  "/fr/services/management-de-transition": "fr",
  "/es/servicios/interim-management": "es",
  // Training & Coaching pages
  "/de/services/training-coaching": "de",
  "/en/services/training-coaching": "en",
  "/fr/services/formation-et-coaching": "fr",
  "/es/servicios/formacion-y-coaching": "es",
};

// Decode pathname for consistent lookups (handles URL-encoded chars + Unicode normalization)
const getSafePath = (pathname: string): string => decodeURIComponent(pathname).normalize("NFC");

export default function Header() {
  const { t, i18n } = useTranslation("home");
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const langDropdownRef = useRef<HTMLDivElement>(null);
  const mobileLangDropdownRef = useRef<HTMLDivElement>(null);

  // Sync i18n language with page URL — URL wins always, never overridden by localStorage
  useEffect(() => {
    const safePath = getSafePath(location.pathname);
    const pageLang = urlToLanguage[safePath];

    if (pageLang && i18n.language !== pageLang) {
      i18n.changeLanguage(pageLang);
    }
  }, [location.pathname, i18n]);

  // Click outside handler for language dropdown
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        langDropdownRef.current &&
        !langDropdownRef.current.contains(e.target as Node) &&
        mobileLangDropdownRef.current &&
        !mobileLangDropdownRef.current.contains(e.target as Node)
      ) {
        setLangDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Escape key handler for language dropdown and mobile menu
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (langDropdownOpen) setLangDropdownOpen(false);
        if (mobileMenuOpen) setMobileMenuOpen(false);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [langDropdownOpen, mobileMenuOpen]);

  // Determine current active language
  const currentLang = urlToLanguage[getSafePath(location.pathname)] || i18n.language || "en";

  const currentLangLabel = languages.find((l) => l.code === currentLang)?.label || "EN";

  const getHomeHref = () => {
    if (currentLang === "en") return "/en";
    if (currentLang === "fr") return "/fr";
    if (currentLang === "es") return "/es";
    return "/de";
  };

  const getAboutHref = () => {
    if (currentLang === "en") return "/en/about-us";
    if (currentLang === "fr") return "/fr/a-propos";
    if (currentLang === "es") return "/es/sobre-nosotros";
    return "/de/ueber-uns";
  };

  const getPartnershipHref = () => {
    if (currentLang === "en") return "/en/partnership";
    if (currentLang === "fr") return "/fr/partenariat";
    if (currentLang === "es") return "/es/colaboracion";
    return "/de/partnerschaft";
  };

  const getServicesHref = () => {
    if (currentLang === "en") return "/en/services";
    if (currentLang === "fr") return "/fr/services";
    if (currentLang === "es") return "/es/servicios";
    return "/de/services";
  };

  const getContactHref = () => {
    if (currentLang === "en") return "/en/contact";
    if (currentLang === "fr") return "/fr/contact";
    if (currentLang === "es") return "/es/contacto";
    return "/de/kontakt";
  };

  const navItems = [
    { label: t("header.nav_home"), href: getHomeHref() },
    { label: t("header.nav_about"), href: getAboutHref() },
    { label: t("header.nav_services"), href: getServicesHref() },
    { label: t("header.nav_partnership"), href: getPartnershipHref() },
    { label: t("header.nav_contact"), href: getContactHref() },
  ];

  const isActive = (href: string) => {
    if (href === "/" || href === "/de" || href === "/en" || href === "/fr" || href === "/es") {
      return location.pathname === href;
    }
    return location.pathname.startsWith(href);
  };

  const handleLanguageChange = (langCode: string) => {
    setLangDropdownOpen(false);
    localStorage.setItem("preferredLang", langCode);
    i18n.changeLanguage(langCode);
    const safePath = getSafePath(location.pathname);
    const pageMap = languagePageMap[safePath];
    if (pageMap && pageMap[langCode]) {
      navigate(pageMap[langCode], { replace: true });
    } else {
      // Fallback: keine Entsprechung gefunden → Startseite in der neuen Sprache
      navigate(`/${langCode}`, { replace: true });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 bg-white`}
    >
      {/* Skip-to-content link for keyboard accessibility (EAA / WCAG 2.4.1) */}
      <a
        href="#main-content"
        onClick={(e) => {
          const main = document.querySelector('main');
          if (main) {
            e.preventDefault();
            main.scrollIntoView({ behavior: 'smooth', block: 'start' });
            main.setAttribute('tabIndex', '-1');
            main.focus({ preventScroll: true });
          }
        }}
        className="sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-500 focus:text-background-50 focus:rounded-md focus:font-heading focus:text-sm focus:font-semibold focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 whitespace-nowrap"
      >
        {currentLang === "de" && "Zum Hauptinhalt springen"}
        {currentLang === "en" && "Skip to main content"}
        {currentLang === "fr" && "Aller au contenu principal"}
        {currentLang === "es" && "Saltar al contenido principal"}
      </a>
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo */}
          <Link to={getHomeHref()} className="flex items-center">
            <div className="flex items-center gap-3">
              <div className="w-44 h-16 md:w-64 md:h-20 flex items-center">
                <img
                  src="https://net-basis.net/Bilder/netbasis-logo-linking-people-business.png"
                  alt={
                    currentLang === "de"
                      ? "NetBasis – linking people & business – Home"
                      : currentLang === "fr"
                        ? "NetBasis – linking people & business – Accueil"
                        : currentLang === "es"
                          ? "NetBasis – linking people & business – Inicio"
                          : "NetBasis – linking people & business – Home"
                  }
                  className="w-full h-full object-contain object-left"
                />
              </div>
            </div>
          </Link>

          {/* Desktop Navigation + Language Switcher */}
          <div className="hidden md:flex items-center gap-2">
            <nav className="flex items-center gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`px-4 py-2 font-heading text-sm font-medium whitespace-nowrap rounded-md transition-colors cursor-pointer ${
                    isActive(item.href)
                      ? "bg-primary-500 text-white hover:bg-primary-600"
                      : "text-foreground-700 hover:text-primary-600 hover:bg-primary-50"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Desktop Language Dropdown */}
            <div className="relative ml-2" ref={langDropdownRef}>
              <button
                onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                className="flex items-center gap-1 px-3 py-2 font-heading text-xs font-semibold rounded-md cursor-pointer transition-colors bg-accent-500 text-foreground-950 hover:bg-accent-600 whitespace-nowrap"
              >
                {currentLangLabel}
                <i className={`${langDropdownOpen ? 'ri-arrow-drop-up-fill' : 'ri-arrow-drop-down-fill'} text-sm`} />
              </button>

              {langDropdownOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-background-200 rounded-md shadow-lg py-1 min-w-[60px] z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`block w-full text-left px-3 py-2 font-heading text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                        currentLang === lang.code
                          ? "bg-primary-50 text-primary-600"
                          : "text-foreground-700 hover:bg-background-100"
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile: Language + Menu */}
          <div className="flex items-center gap-2 md:hidden">
            {/* Mobile Language Dropdown */}
            <div className="relative" ref={mobileLangDropdownRef}>
              <button
                onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                className="flex items-center gap-1 px-3 py-2 font-heading text-xs font-semibold rounded-md cursor-pointer transition-colors bg-accent-500 text-foreground-950 whitespace-nowrap"
              >
                {currentLangLabel}
                <i className={`${langDropdownOpen ? 'ri-arrow-drop-up-fill' : 'ri-arrow-drop-down-fill'} text-sm`} />
              </button>

              {langDropdownOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-background-200 rounded-md shadow-lg py-1 min-w-[60px] z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`block w-full text-left px-3 py-2 font-heading text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                        currentLang === lang.code
                          ? "bg-primary-50 text-primary-600"
                          : "text-foreground-700 hover:bg-background-100"
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-10 h-10 flex items-center justify-center rounded-md hover:bg-primary-50 transition-colors cursor-pointer"
              aria-label="Menu"
            >
              <i className="ri-menu-line text-foreground-700 text-xl" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-background-200 shadow-lg">
          <nav className="flex flex-col py-2">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-3 font-heading text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  isActive(item.href)
                    ? "bg-primary-500 text-white"
                    : "text-foreground-700 hover:bg-primary-50"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}